import { Link, useParams } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Check,
  MapPin,
  Camera,
  Search,
  FileText,
  Shield,
} from "lucide-react";
import { LOCAL_AREAS, LOCAL_AREAS_LIST } from "../data/localAreas";
import { COMPANY } from "../data/site";
import SEO from "../components/SEO";
import ContactCTA from "../components/ContactCTA";
import FAQ from "../components/FAQ";

const PROCESS_STEPS = [
  {
    icon: Camera,
    title: "Skicka en bild",
    text: "Beskriv kort var trädet står och vad du vill ha hjälp med.",
  },
  {
    icon: Search,
    title: "Första bedömning",
    text: "Vi tittar på bilderna och bedömer plats, risker och metod.",
  },
  {
    icon: FileText,
    title: "Tydlig offert",
    text: "Du får ett tydligt pris och vet exakt vad som ingår.",
  },
  {
    icon: Shield,
    title: "Säkert utförande",
    text: "Vi planerar tid och utför arbetet säkert och städat.",
  },
];

const SERVICE_LINKS = [
  { to: "/tradfallning", label: "Trädfällning" },
  { to: "/stubbfrasning", label: "Stubbfräsning" },
  { to: "/beskarning", label: "Beskärning" },
  { to: "/bortforsling", label: "Bortforsling" },
  { to: "/kontakt", label: "Kontakt och offert" },
];

/**
 * Single template that renders ANY local SEO area using the data file.
 * Bound to routes via Route element={<LocalAreaPage areaKey="bastad" />}.
 */
export default function LocalAreaPage({ areaKey }) {
  const params = useParams();
  const key = areaKey || params.area;
  const area = LOCAL_AREAS[key];
  const otherAreas = area
    ? LOCAL_AREAS_LIST.filter((item) => item.slug !== area.slug)
    : [];

  if (!area) {
    return (
      <div className="section-pad text-center">
        <h1 className="font-heading text-3xl font-bold">
          Området hittades inte
        </h1>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={area.title}
        description={area.metaDescription}
        path={`/${area.slug}`}
        image={`${COMPANY.primaryDomain}${area.image}`}
      />

      {/* HERO */}
      <section
        data-testid="local-hero"
        className="relative min-h-[400px] md:min-h-[480px] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={area.image}
            alt={`Trädfällning i ${area.city}`}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 tf-hero-overlay" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-white">
          <span className="eyebrow !text-[#F2A57C] inline-flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> {area.city}
          </span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.06] tracking-tight max-w-4xl tf-hero-text-shadow">
            {area.h1}
          </h1>
          <p className="mt-4 text-[#E8EAE5] text-base md:text-lg leading-relaxed max-w-3xl tf-hero-text-shadow">
            {area.intro}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/kontakt#offert"
              data-testid="local-cta-quote"
              className="inline-flex items-center justify-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors shadow-lg shadow-black/20"
            >
              Få offert <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneE164}`}
              data-testid="local-cta-phone"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/85 hover:bg-white hover:text-[#1B2A1E] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors backdrop-blur-sm"
            >
              <Phone className="w-4 h-4" /> Ring {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* LOCAL HJÄLP I [ORT] */}
      <section className="section-pad" data-testid="local-help-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <span className="eyebrow">Lokalt</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1B2A1E] leading-tight mb-5">
              Lokal hjälp i {area.city}
            </h2>
            {area.body.map((para, i) => (
              <p
                key={i}
                className="text-[#3a4338] text-lg leading-relaxed mb-5"
                data-testid={`local-body-${i + 1}`}
              >
                {para}
              </p>
            ))}
          </div>

          <ul className="lg:col-span-5 space-y-3">
            {area.bullets.map((b, i) => (
              <li
                key={i}
                data-testid={`local-bullet-${i + 1}`}
                className="flex items-start gap-3 bg-white border border-[#E2E0D8] rounded-lg p-4"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1B2A1E] text-[#D95D2E] shrink-0 mt-0.5">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
                <span className="text-[#18221A] font-medium leading-relaxed">
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VANLIGA UPPDRAG */}
      <section
        className="section-pad bg-[#EFEBDF]"
        data-testid="local-jobs-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <span className="eyebrow">Exempel</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1B2A1E] leading-tight">
              Vanliga uppdrag i {area.city}
            </h2>
            {area.commonJobsIntro && (
              <p className="mt-4 text-[#3a4338] text-lg leading-relaxed">
                {area.commonJobsIntro}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {area.commonJobs.map((job, i) => (
              <div
                key={i}
                data-testid={`local-job-${i + 1}`}
                className="flex items-start gap-3 bg-white border border-[#E2E0D8] rounded-lg px-5 py-4"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#D95D2E]/10 text-[#D95D2E] shrink-0 mt-0.5 font-heading font-extrabold text-sm tabular-nums">
                  {i + 1}
                </span>
                <span className="text-[#18221A] leading-relaxed">{job}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SÅ GÅR DET TILL */}
      <section className="section-pad" data-testid="local-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <span className="eyebrow">Så går det till</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1B2A1E] leading-tight">
              Från bild till färdigt arbete — fyra steg
            </h2>
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {PROCESS_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.title}
                  data-testid={`local-step-${i + 1}`}
                  className="bg-white border border-[#E2E0D8] rounded-xl p-6 flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-[#1B2A1E] text-white">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </span>
                    <span className="font-heading text-3xl font-extrabold text-[#D95D2E]/20 tabular-nums">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#1B2A1E] mb-2">
                    {step.title}
                  </h3>
                  <p className="text-[15px] text-[#3a4338] leading-relaxed">
                    {step.text}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* TJÄNSTELÄNKAR */}
      <section
        className="pb-16 md:pb-20"
        data-testid="local-services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-[#1B2A1E] mb-5">
            Våra tjänster
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {SERVICE_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`local-service-${link.to.replace("/", "")}`}
                className="inline-flex items-center justify-between bg-white border border-[#E2E0D8] hover:border-[#D95D2E] rounded-lg px-4 py-3 text-[#1B2A1E] font-semibold transition-colors"
              >
                {link.label}
                <ArrowRight className="w-4 h-4 text-[#D95D2E]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ANDRA OMRÅDEN */}
      <section
        className="pb-16 md:pb-20"
        data-testid="local-other-areas-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-[#1B2A1E] mb-2">
            Fler områden vi arbetar i
          </h2>
          <p className="text-[#3a4338] mb-5">
            Vi hjälper även till med trädfällning i andra orter runt Laholm.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {otherAreas.map((otherArea) => (
              <Link
                key={otherArea.slug}
                to={`/${otherArea.slug}`}
                data-testid={`local-other-area-${otherArea.slug}`}
                className="group inline-flex items-center justify-between gap-3 bg-white border border-[#E2E0D8] hover:border-[#D95D2E] rounded-lg px-4 py-3 text-[#1B2A1E] font-semibold transition-colors"
              >
                <span className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#D95D2E]" />
                  Trädfällning i {otherArea.city}
                </span>
                <ArrowRight className="w-4 h-4 text-[#D95D2E] shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {area.faq && area.faq.length > 0 && (
        <FAQ
          id={`local-faq-${key}`}
          title={`Vanliga frågor om trädfällning i ${area.city}`}
          items={area.faq}
        />
      )}

      <ContactCTA
        title={`Behöver du hjälp med trädfällning i ${area.city}?`}
        text="Skicka gärna en bild på trädet och beskriv var det står, så blir det lättare för oss att göra en första bedömning."
      />
    </>
  );
}
